import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Expand } from "lucide-react";
import Section from "./Section";
import Lightbox from "./Lightbox";
import { useCooldownCarousel } from "../useCooldownCarousel";
import { galleryImages, translations, type Lang } from "../content";

export default function Gallery({ lang }: { lang: Lang }) {
  const t = translations[lang].gallery;
  const rtl = lang === "he";

  const [expanded, setExpanded] = useState(false); // wheel <-> full grid
  const [lightbox, setLightbox] = useState<number | null>(null);

  const { emblaRef, scrollPrev, scrollNext } = useCooldownCarousel({
    align: "start",
    loop: true,
    dragFree: true,
    direction: rtl ? "rtl" : "ltr",
  });

  const tile = (src: string, i: number, className: string) => (
    <motion.button
      key={i}
      type="button"
      onClick={() => setLightbox(i)}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5 }}
      aria-label={`${t.title} ${i + 1}`}
      className={`relative overflow-hidden group cursor-zoom-in ${className}`}
    >
      <img
        src={src}
        alt={`${t.title} ${i + 1}`}
        loading="lazy"
        decoding="async"
        draggable={false}
        className="w-full h-full object-cover grayscale-[0.3] transition-all duration-700
                   group-hover:grayscale-0 group-hover:scale-[1.04]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent
                      opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <span className="absolute bottom-3 ltr:right-3 rtl:left-3 p-2 rounded-full bg-black/60 text-primary
                       opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0
                       transition-all duration-300">
        <Expand className="w-4 h-4" />
      </span>
    </motion.button>
  );

  return (
    <Section id="gallery" compact>
      <div dir={rtl ? "rtl" : "ltr"}>
        {expanded ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {galleryImages.map((src, i) => tile(src, i, "aspect-[4/3] rounded-xl"))}
          </div>
        ) : (
          /* The wheel: drag or swipe to spin, autoplays with a 5s idle cooldown.
             Arrows sit against the physical left and right edges of the carousel. */
          <div className="overflow-hidden cursor-grab active:cursor-grabbing" ref={emblaRef}>
            <div className="flex ltr:-ml-3 rtl:-mr-3 md:ltr:-ml-4 md:rtl:-mr-4">
              {galleryImages.map((src, i) => (
                <div
                  key={i}
                  className="ltr:pl-3 rtl:pr-3 md:ltr:pl-4 md:rtl:pr-4 min-w-0
                             flex-[0_0_78%] sm:flex-[0_0_52%] lg:flex-[0_0_36%] xl:flex-[0_0_29%]"
                >
                  {tile(src, i, "aspect-[4/3] rounded-xl w-full")}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Control row: arrows at the outer edges, the toggle centred between them.
            Forced to LTR so DOM order matches physical left/right in both languages. */}
        <div dir="ltr" className="mt-8 flex items-center justify-between gap-4">
          {!expanded ? (
            /* In RTL the next slide arrives from the left, so the arrows swap roles. */
            <button
              onClick={rtl ? scrollNext : scrollPrev}
              aria-label={rtl ? "Next" : "Previous"}
              className="p-3 md:p-4 rounded-full border border-border/15 text-foreground/80
                         hover:text-primary hover:border-primary/50 transition-colors shrink-0"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          ) : (
            <span className="w-12 md:w-14 shrink-0" aria-hidden />
          )}

          <button
            onClick={() => setExpanded((v) => !v)}
            dir={rtl ? "rtl" : "ltr"}
            className="pill-ghost group px-0 text-center"
          >
            {expanded ? t.showLess : t.showAll}
            <span className="inline-block transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1">
              {rtl ? "←" : "→"}
            </span>
          </button>

          {!expanded ? (
            <button
              onClick={rtl ? scrollPrev : scrollNext}
              aria-label={rtl ? "Previous" : "Next"}
              className="p-3 md:p-4 rounded-full border border-border/15 text-foreground/80
                         hover:text-primary hover:border-primary/50 transition-colors shrink-0"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          ) : (
            <span className="w-12 md:w-14 shrink-0" aria-hidden />
          )}
        </div>
      </div>

      <Lightbox
        images={galleryImages}
        index={lightbox}
        onClose={() => setLightbox(null)}
        onNavigate={setLightbox}
        rtl={rtl}
      />
    </Section>
  );
}
